/**
 * HQ's share-subject directory: what the dashboard's Share dialog offers as
 * pickers (users, roles, departments) instead of free text.
 *
 * The answer is computed by the API's `dashboard-share-subjects` service
 * (packages/api/src/services/dashboard/share-subjects.ts), so one place decides
 * the key a share stores (a user's id, a role or department NAME), whether a
 * subject could ever edit a board (HQ's tier resolver), and which accounts are
 * switched off. This file only fetches it; it holds no state.
 *
 * Installed on the RootStore by installDashboardHost (dashboardHost.ts), under
 * the key the package reads structurally.
 */
import {
  DEFAULT_DASHBOARD_NAMESPACE,
  shareDirectoryKey,
  type FeathersClientLike,
  type ShareSubjectDirectory,
  type ShareSubjectOption,
} from "@intrasync/dashboard";

export type { ShareSubjectDirectory, ShareSubjectOption };

/** `shareDirectoryKey(ns)` for HQ's default `dashboard` mount: where the Share dialog looks. */
export const SHARE_DIRECTORY_KEY = shareDirectoryKey(DEFAULT_DASHBOARD_NAMESPACE);

export const SHARE_SUBJECTS_PATH = "dashboard-share-subjects";

interface FindableService {
  find(params?: { query?: Record<string, unknown> }): Promise<unknown>;
}

/** The directory over HQ's Feathers client. A failed read rejects: the dialog says so rather than showing an empty list. */
export function createShareDirectory(app: FeathersClientLike): ShareSubjectDirectory {
  return {
    async list(type) {
      const service = app.service(SHARE_SUBJECTS_PATH) as FindableService;
      const answer = await service.find({ query: { type } });
      if (!Array.isArray(answer)) {
        throw new Error(`${SHARE_SUBJECTS_PATH} answered something other than a list for "${type}".`);
      }
      return answer as ShareSubjectOption[];
    },
  };
}
